import * as THREE from 'three';
import { gameConfig } from '../config/gameConfig.js';

export class Controls {
    constructor(scene, game) {
        this.scene = scene;
        this.game = game;
        this.raycaster = new THREE.Raycaster();
        this.mouse = new THREE.Vector2();
        this.isDragging = false;
        this.previousMousePosition = {
            x: 0,
            y: 0
        };
        this.isAnimating = false; // 添加旋转动画状态标志
        
        // 添加箭头指示器相关属性
        this.arrowHelper = null;
        this.touchStartPosition = new THREE.Vector3();
        this.touchCurrentPosition = new THREE.Vector3();
        
        // 计算滑动距离阈值为屏幕宽度的四分之一
        this.swipeThreshold = window.innerWidth / 4;
        
        // 长按相关属性
        this.longPressTimer = null;
        this.longPressDuration = gameConfig.dice.teleport.longPressDuration; // 从配置文件读取长按时间
        this.isDraggingTeleport = false;
        this.draggedDice = null;
        this.originalDicePosition = new THREE.Vector3();
        
        // 创建长按提示元素
        this.dragHintElement = document.createElement('div');
        this.dragHintElement.style.position = 'fixed';
        this.dragHintElement.style.top = '20%';
        this.dragHintElement.style.left = '50%';
        this.dragHintElement.style.transform = 'translate(-50%, -50%)';
        this.dragHintElement.style.fontSize = '24px';
        this.dragHintElement.style.color = '#fff';
        this.dragHintElement.style.textShadow = '2px 2px 4px rgba(0,0,0,0.5)';
        this.dragHintElement.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
        this.dragHintElement.style.padding = '10px 20px';
        this.dragHintElement.style.borderRadius = '10px';
        this.dragHintElement.style.fontWeight = 'bold';
        this.dragHintElement.style.zIndex = '9999';
        this.dragHintElement.style.display = 'none';
        document.body.appendChild(this.dragHintElement);
        
        this.initializeEventListeners();
    }

    initializeEventListeners() {
        const canvas = this.scene.renderer.domElement;
        
        // 添加键盘事件监听
        window.addEventListener('keydown', (event) => this.handleKeyDown(event));
        
        // 触摸事件
        canvas.addEventListener('touchstart', (event) => this.handleTouchStart(event), { passive: false });
        canvas.addEventListener('touchmove', (event) => this.handleTouchMove(event), { passive: false });
        canvas.addEventListener('touchend', () => this.handleTouchEnd());
    }

    handleKeyDown(event) {
        if (!this.game.isGameStarted || !this.game.selectedDice || this.isAnimating) return;

        const selectedDice = this.game.selectedDice;
        
        // 如果是炸弹骰子，不允许旋转
        if (selectedDice.type === 'bomb') return;

        const rotationAmount = Math.PI / 2;

        // 创建目标四元数和旋转轴
        const targetQuaternion = new THREE.Quaternion();
        const axis = new THREE.Vector3();

        // 根据按键设置旋转轴和方向
        let rotationDirection = '';
        switch(event.key) {
            case 'ArrowUp':
                axis.set(1, 0, 0); // 绕X轴（平行于屏幕）
                targetQuaternion.setFromAxisAngle(axis, -rotationAmount); // 向上旋转为负角度
                rotationDirection = '向上';
                break;
            case 'ArrowDown':
                axis.set(1, 0, 0); // 绕X轴（平行于屏幕）
                targetQuaternion.setFromAxisAngle(axis, rotationAmount); // 向下旋转为正角度
                rotationDirection = '向下';
                break;
            case 'ArrowLeft':
                axis.set(0, 0, 1); // 绕Z轴（垂直于屏幕）
                targetQuaternion.setFromAxisAngle(axis, rotationAmount); // 向左旋转为负角度
                rotationDirection = '向左';
                break;
            case 'ArrowRight':
                axis.set(0, 0, 1); // 绕Z轴（垂直于屏幕）
                targetQuaternion.setFromAxisAngle(axis, -rotationAmount); // 向右旋转为正角度
                rotationDirection = '向右';
                break;
            default:
                return;
        }

        this.isAnimating = true; // 设置动画开始标志

        // 打印旋转前的棋盘状态
        this.game.board.printBoardState('旋转前的棋盘状态（顶部点数）');

        // 打印旋转前的骰子几何中心坐标
        const startBox = new THREE.Box3().setFromObject(selectedDice.mesh);
        const startCenter = new THREE.Vector3();
        startBox.getCenter(startCenter);
        console.log('旋转前骰子的几何中心坐标:', {
            x: startCenter.x.toFixed(2),
            y: startCenter.y.toFixed(2),
            z: startCenter.z.toFixed(2)
        });

        // 保存当前世界坐标系中的旋转状态
        const startQuaternion = selectedDice.mesh.quaternion.clone();
        
        // 计算最终旋转状态（在世界坐标系中应用旋转）
        const finalQuaternion = targetQuaternion.multiply(startQuaternion);
        
        const duration = 800; // 动画持续时间（毫秒）
        const startTime = Date.now();

        console.log(`开始${rotationDirection}旋转骰子:`, {
            初始欧拉角: {
                x: THREE.MathUtils.radToDeg(selectedDice.mesh.rotation.x).toFixed(2) + '°',
                y: THREE.MathUtils.radToDeg(selectedDice.mesh.rotation.y).toFixed(2) + '°',
                z: THREE.MathUtils.radToDeg(selectedDice.mesh.rotation.z).toFixed(2) + '°'
            },
            初始四元数: {
                x: startQuaternion.x.toFixed(3),
                y: startQuaternion.y.toFixed(3),
                z: startQuaternion.z.toFixed(3),
                w: startQuaternion.w.toFixed(3)
            },
            目标四元数: {
                x: targetQuaternion.x.toFixed(3),
                y: targetQuaternion.y.toFixed(3),
                z: targetQuaternion.z.toFixed(3),
                w: targetQuaternion.w.toFixed(3)
            },
            最终四元数: {
                x: finalQuaternion.x.toFixed(3),
                y: finalQuaternion.y.toFixed(3),
                z: finalQuaternion.z.toFixed(3),
                w: finalQuaternion.w.toFixed(3)
            },
            旋转轴: {
                x: axis.x.toFixed(2),
                y: axis.y.toFixed(2),
                z: axis.z.toFixed(2)
            },
            旋转角度: THREE.MathUtils.radToDeg(rotationAmount).toFixed(2) + '°',
            骰子面值: {
                右面: selectedDice.faces[0],
                左面: selectedDice.faces[1],
                上面: selectedDice.faces[2],
                下面: selectedDice.faces[3],
                前面: selectedDice.faces[4],
                后面: selectedDice.faces[5]
            }
        });

        const animate = () => {
            const progress = (Date.now() - startTime) / duration;
            if (progress >= 1) {
                selectedDice.mesh.quaternion.copy(finalQuaternion);
                
                // 打印旋转后的骰子几何中心坐标
                const endBox = new THREE.Box3().setFromObject(selectedDice.mesh);
                const endCenter = new THREE.Vector3();
                endBox.getCenter(endCenter);
                console.log('旋转后骰子的几何中心坐标:', {
                    x: endCenter.x.toFixed(2),
                    y: endCenter.y.toFixed(2),
                    z: endCenter.z.toFixed(2)
                });
                
                // 根据旋转方向更新骰子面值
                switch(event.key) {
                    case 'ArrowUp':
                        selectedDice.rotateFaces('up');
                        break;
                    case 'ArrowDown':
                        selectedDice.rotateFaces('down');
                        break;
                    case 'ArrowLeft':
                        selectedDice.rotateFaces('left');
                        break;
                    case 'ArrowRight':
                        selectedDice.rotateFaces('right');
                        break;
                }
                
                console.log(`${rotationDirection}旋转完成:`, {
                    最终欧拉角: {
                        x: THREE.MathUtils.radToDeg(selectedDice.mesh.rotation.x).toFixed(2) + '°',
                        y: THREE.MathUtils.radToDeg(selectedDice.mesh.rotation.y).toFixed(2) + '°',
                        z: THREE.MathUtils.radToDeg(selectedDice.mesh.rotation.z).toFixed(2) + '°'
                    },
                    最终四元数: {
                        x: selectedDice.mesh.quaternion.x.toFixed(3),
                        y: selectedDice.mesh.quaternion.y.toFixed(3),
                        z: selectedDice.mesh.quaternion.z.toFixed(3),
                        w: selectedDice.mesh.quaternion.w.toFixed(3)
                    },
                    骰子面值: {
                        右面: selectedDice.faces[0],
                        左面: selectedDice.faces[1],
                        上面: selectedDice.faces[2],
                        下面: selectedDice.faces[3],
                        前面: selectedDice.faces[4],
                        后面: selectedDice.faces[5]
                    }
                });
                this.isAnimating = false; // 动画完成，重置标志
                
                // 减少剩余步数并更新显示
                this.game.remainingSteps--;
                this.game.updateStepsDisplay();
                
                // 打印当前棋盘状态
                this.game.board.printBoardState('当前棋盘状态（顶部点数）');
                
                // 在骰子旋转完成后检查是否可以合并
                console.log('开始检查骰子是否可以合并...');
                this.game.checkAndMergeDice();
                return;
            }

            // 使用缓动函数使动画更平滑
            const easeProgress = 1 - Math.pow(1 - progress, 2);

            // 使用四元数插值实现平滑旋转
            selectedDice.mesh.quaternion.copy(startQuaternion.clone().slerp(finalQuaternion, easeProgress));

            requestAnimationFrame(animate);
        };

        animate();
    }



    // 触摸事件处理
    handleTouchStart(event) {
        // 如果是双指触摸，不处理骰子旋转
        if (event.touches.length > 1) return;
    
        event.preventDefault();
        const touch = event.touches[0];
        
        // 获取触摸点在世界坐标系中的位置
        const rect = event.target.getBoundingClientRect();
        const x = ((touch.clientX - rect.left) / rect.width) * 2 - 1;
        const y = -((touch.clientY - rect.top) / rect.height) * 2 + 1;
        
        console.log('触摸开始:', {
            触摸坐标: {
                clientX: touch.clientX,
                clientY: touch.clientY
            },
            归一化坐标: { x, y }
        });
        
        this.touchStartPosition.set(x, y, 0);
        this.touchStartPosition.unproject(this.scene.camera);
        this.touchCurrentPosition.copy(this.touchStartPosition);
        
        // 创建箭头指示器
        const direction = new THREE.Vector3(0, 0, 0);
        const origin = this.touchStartPosition.clone();
        const length = 0;
        const color = 0xffff00; // 黄色
        
        // 如果已存在箭头，先移除
        if (this.arrowHelper) {
            this.scene.scene.remove(this.arrowHelper);
        }
        
        this.arrowHelper = new THREE.ArrowHelper(direction, origin, length, color, 0.5, 0.3);
        this.scene.scene.add(this.arrowHelper);
        
        // 处理骰子的选择
        this.mouse.x = x;
        this.mouse.y = y;
        this.raycaster.setFromCamera(this.mouse, this.scene.camera);
        
        const diceObjects = [];
        this.game.board.matrix.forEach(row => {
            if (row) {
                row.forEach(dice => {
                    if (dice && dice.mesh) {
                        diceObjects.push(dice.mesh);
                    }
                });
            }
        });
        
        const intersects = this.raycaster.intersectObjects(diceObjects);
        
        // 记录触摸开始时是否点击到了骰子
        this.touchStartOnDice = false;
        if (intersects.length > 0) {
            const clickedMesh = intersects[0].object;
            let clickedDice = null;
            
            outerLoop: for (let i = 0; i < this.game.board.matrix.length; i++) {
                for (let j = 0; j < this.game.board.matrix[i].length; j++) {
                    const dice = this.game.board.matrix[i][j];
                    if (dice && this.findClickedDiceByMesh(clickedMesh, dice)) {
                        clickedDice = dice;
                        break outerLoop;
                    }
                }
            }
            
            if (clickedDice) {
                this.touchStartOnDice = true;
                this.touchStartDice = clickedDice;
                
                // 如果点击的是已选中的传送骰子，启动长按计时器
                if (this.game.selectedDice === clickedDice && clickedDice.isTeleport) {
                    // 保存原始位置
                    this.originalDicePosition = clickedDice.originalPosition;
                    
                    // 显示长按提示
                    this.dragHintElement.textContent = '长按中...';
                    this.dragHintElement.style.display = 'block';
                    
                    // 设置长按计时器
                    let countdownTime = this.longPressDuration / 1000;
                    this.dragHintElement.textContent = `长按中...${countdownTime}秒`;
                    
                    const countdownInterval = setInterval(() => {
                        countdownTime -= 0.1;
                        if (countdownTime > 0) {
                            this.dragHintElement.textContent = `长按中...${countdownTime.toFixed(1)}秒`;
                        } else {
                            clearInterval(countdownInterval);
                        }
                    }, 100);
                    
                    this.longPressTimer = setTimeout(() => {
                        this.isDraggingTeleport = true;
                        this.draggedDice = clickedDice;
                        this.dragHintElement.textContent = '可以拖动';
                        console.log('开始拖动传送骰子');
                        clearInterval(countdownInterval);
                    }, this.longPressDuration);
                }
            }
        }
        
        // 更新拖动状态
        this.isDragging = false;
        this.previousMousePosition = {
            x: touch.clientX,
            y: touch.clientY
        };
}

    handleTouchMove(event) {
        // 如果是双指触摸，不处理骰子旋转
        if (event.touches.length > 1) return;
    
        event.preventDefault();
        const touch = event.touches[0];
        
        // 如果正在拖动传送骰子
        if (this.isDraggingTeleport && this.draggedDice) {
            // 更新骰子位置
            const rect = event.target.getBoundingClientRect();
            const x = ((touch.clientX - rect.left) / rect.width) * 2 - 1;
            const y = -((touch.clientY - rect.top) / rect.height) * 2 + 1;
            
            console.log('传送骰子拖动中:', {
                触摸坐标: {
                    clientX: touch.clientX,
                    clientY: touch.clientY
                },
                归一化坐标: { x, y },
                骰子当前位置: {
                    x: this.draggedDice.mesh.position.x.toFixed(2),
                    y: this.draggedDice.mesh.position.y.toFixed(2),
                    z: this.draggedDice.mesh.position.z.toFixed(2)
                }
            });
            // 将屏幕坐标转换为世界坐标
            const vector = new THREE.Vector3(x, y, 0.5);
            vector.unproject(this.scene.camera);
            
            // 计算射线与平面的交点
            const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
            const raycaster = new THREE.Raycaster();
            raycaster.setFromCamera(new THREE.Vector2(x, y), this.scene.camera);
            
            const intersection = new THREE.Vector3();
            raycaster.ray.intersectPlane(plane, intersection);
            
            // 更新骰子位置
            this.draggedDice.mesh.position.x = intersection.x;
            this.draggedDice.mesh.position.z = intersection.z;
            this.draggedDice.mesh.position.y = 3.5; // 保持固定高度
            
            return;
        }
        
        // 更新当前触摸点位置
        const rect = event.target.getBoundingClientRect();
        const x = ((touch.clientX - rect.left) / rect.width) * 2 - 1;
        const y = -((touch.clientY - rect.top) / rect.height) * 2 + 1;
        
        this.touchCurrentPosition.set(x, y, 0);
        this.touchCurrentPosition.unproject(this.scene.camera);
        
        // 更新箭头方向和长度
        if (this.arrowHelper) {
            const direction = this.touchCurrentPosition.clone().sub(this.touchStartPosition).normalize();
            const length = this.touchCurrentPosition.distanceTo(this.touchStartPosition);
            
            console.log('滑动状态:', {
                距离: length.toFixed(3),
                方向向量: {
                    x: direction.x.toFixed(3),
                    y: direction.y.toFixed(3),
                    z: direction.z.toFixed(3)
                }
            });
            
            this.arrowHelper.setDirection(direction);
            this.arrowHelper.setLength(length, length * 0.2, length * 0.1);
            
            // 计算实际的像素距离
            const pixelDistance = Math.sqrt(
                Math.pow(touch.clientX - this.previousMousePosition.x, 2) +
                Math.pow(touch.clientY - this.previousMousePosition.y, 2)
            );
            
            // 如果有选中的骰子且滑动距离超过阈值，触发旋转
            if (this.game.selectedDice && pixelDistance > this.swipeThreshold && !this.isAnimating) {
                // 计算滑动方向
                const dx = touch.clientX - this.previousMousePosition.x;
                const dy = touch.clientY - this.previousMousePosition.y;
                
                // 确定主要滑动方向
                if (Math.abs(dx) > Math.abs(dy)) {
                    // 水平滑动
                    const event = { key: dx > 0 ? 'ArrowRight' : 'ArrowLeft' };
                    this.handleKeyDown(event);
                } else {
                    // 垂直滑动
                    const event = { key: dy > 0 ? 'ArrowDown' : 'ArrowUp' };
                    this.handleKeyDown(event);
                }
                
                // 移除箭头指示器
                this.scene.scene.remove(this.arrowHelper);
                this.arrowHelper = null;
            }
        }
    }

    async handleTouchEnd() {
        // 如果是双指触摸，不处理骰子旋转
        if (event.touches && event.touches.length > 1) return;
    
        console.log('触摸结束');
        
        // 移除箭头指示器
        if (this.arrowHelper) {
            this.scene.scene.remove(this.arrowHelper);
            this.arrowHelper = null;
        }
        
        // 清除长按计时器
        if (this.longPressTimer) {
            clearTimeout(this.longPressTimer);
            this.longPressTimer = null;
        }
        
        // 隐藏拖动提示元素
        this.dragHintElement.style.display = 'none';
        // 如果正在拖动传送骰子，检查是否有目标骰子
        if (this.isDraggingTeleport && this.draggedDice) {
            // 检查是否允许拖拽交换
            if (!gameConfig.dice.teleport.enableDragSwap) {
                console.log('拖拽交换功能已禁用');
                
                // 让传送骰子回到原位
                const startPosition = this.draggedDice.mesh.position.clone();
                const targetPosition = this.originalDicePosition.clone();
                const duration = 500;
                const startTime = Date.now();
                
                console.log('传送骰子拖动结束，开始回到原位动画');
                
                const animateDiceReturn = () => {
                    const elapsed = Date.now() - startTime;
                    const progress = Math.min(elapsed / duration, 1);
                    
                    const easeProgress = 1 - Math.pow(1 - progress, 3);
                    
                    this.draggedDice.mesh.position.lerpVectors(startPosition, targetPosition, easeProgress);
                    
                    if (progress < 1) {
                        requestAnimationFrame(animateDiceReturn);
                    } else {
                        this.draggedDice.mesh.position.copy(targetPosition);
                        console.log('传送骰子回到原位动画完成');
                        
                        this.isDraggingTeleport = false;
                        this.draggedDice = null;
                    }
                };
                
                animateDiceReturn();
                return;
            }
            
            // 创建射线检测目标位置是否有骰子
            const rect = this.scene.renderer.domElement.getBoundingClientRect();
            console.log('Canvas位置和大小:', {
                left: rect.left,
                top: rect.top,
                width: rect.width,
                height: rect.height
            });
            
            console.log('拖动骰子当前位置:', {
                x: this.draggedDice.mesh.position.x,
                y: this.draggedDice.mesh.position.y,
                z: this.draggedDice.mesh.position.z
            });
            
            // 将3D位置投影到屏幕上
            const vector = new THREE.Vector3();
            vector.setFromMatrixPosition(this.draggedDice.mesh.matrixWorld);
            vector.project(this.scene.camera);
            
            const x = vector.x;
            const y = vector.y;
            
            console.log('投影后的归一化坐标:', { x, y });
            
            this.raycaster.setFromCamera(new THREE.Vector2(x, y), this.scene.camera);
            
            // 收集所有非当前拖动的骰子的mesh对象
            const meshObjects = [];
            this.game.board.matrix.forEach(row => {
                if (row) {
                    row.forEach(dice => {
                        if (dice && dice.mesh && dice !== this.draggedDice) {
                            meshObjects.push(dice.mesh);
                        }
                    });
                }
            });
            
            console.log('可检测的骰子对象数量:', meshObjects.length);
            
            const intersects = this.raycaster.intersectObjects(meshObjects);
            console.log('射线检测到的相交对象数量:', intersects.length);
            
            if (intersects.length > 0) {
                // 找到目标骰子
                const targetMesh = intersects[0].object;
                console.log('找到相交的目标骰子:', {
                    distance: intersects[0].distance,
                    point: intersects[0].point
                });
                
                // 直接从mesh的userData中获取骰子实例
                const targetDice = targetMesh.userData.dice;
                
                // 查找目标骰子在矩阵中的位置
                let targetPosition = null;
                outerLoop: for (let i = 0; i < this.game.board.matrix.length; i++) {
                    for (let j = 0; j < this.game.board.matrix[i].length; j++) {
                        if (this.game.board.matrix[i][j] === targetDice) {
                            targetPosition = { i, j };
                            break outerLoop;
                        }
                    }
                }
                
                if (targetDice && targetPosition) {
                    console.log('找到目标骰子:', {
                        position: targetPosition,
                        isBomb: targetDice.isBomb,
                        isTeleport: targetDice.isTeleport,
                        faces: targetDice.faces
                    });
                    

                    const teleportDice = this.game.selectedDice;
                    this.game.selectedDice.setHighlight(false)
                    this.game.selectedDice = null;

                    this.draggedDice.setHighlight(false)
                    this.draggedDice = null;

                    // 执行交换操作
                    console.log('开始执行骰子交换操作');
                    // 先将拖动的骰子位置重置为原始位置，确保交换动画的起点正确
                    // this.draggedDice.mesh.position.copy(this.originalDicePosition);
                    await this.game.board.swapDicePositions(teleportDice, targetDice);
                    
                    // 减少剩余步数并更新显示
                    this.game.remainingSteps--;
                    this.game.updateStepsDisplay();
                    

                    // 拖动结束后检查并合并骰子
                    console.log('拖动结束后检查并合并骰子')
                    this.game.checkAndMergeDice()

                    return;
                } else {
                    console.log('未能找到有效的目标骰子或其位置');
                }
            } else {
                console.log('射线检测未找到任何目标骰子');
            }
            
            // 如果没有找到目标骰子，让传送骰子回到原位
            const startPosition = this.draggedDice.mesh.position.clone();
            const targetPosition = this.originalDicePosition.clone();
            const duration = 500;
            const startTime = Date.now();
            
            console.log('传送骰子拖动结束，开始回到原位动画');
            console.log('起始位置:', startPosition);
            console.log('目标位置:', targetPosition);
            
            const animateDiceReturn = () => {
                const elapsed = Date.now() - startTime;
                const progress = Math.min(elapsed / duration, 1);
                
                const easeProgress = 1 - Math.pow(1 - progress, 3);
                
                this.draggedDice.mesh.position.lerpVectors(startPosition, targetPosition, easeProgress);
                
                if (progress < 1) {
                    requestAnimationFrame(animateDiceReturn);
                } else {
                    this.draggedDice.mesh.position.copy(targetPosition);
                    console.log('传送骰子回到原位动画完成');
                    
                    this.isDraggingTeleport = false;
                    this.draggedDice = null;
                }
            };
            
            animateDiceReturn();
            return;
        }
    
        // 计算滑动距离和方向
        const distance = this.touchCurrentPosition.distanceTo(this.touchStartPosition);
        const direction = this.touchCurrentPosition.clone().sub(this.touchStartPosition);
    
        // 如果有选中的骰子
        if (this.touchStartOnDice && this.touchStartDice) {
            // 设置滑动距离阈值，小于此值视为点击
            const clickThreshold = 0.1;
    
            if (distance < clickThreshold) {
                // 视为点击操作
                await this.game.handleClick(this.touchStartDice);
            } else {
                // 根据滑动方向确定旋转方向
                const absX = Math.abs(direction.x);
                const absY = Math.abs(direction.y);
    
                // 模拟按键事件
                const event = {
                    key: absX > absY
                        ? (direction.x > 0 ? 'ArrowRight' : 'ArrowLeft')
                        : (direction.y > 0 ? 'ArrowDown' : 'ArrowUp')
                };
    
                // 触发旋转
                this.handleKeyDown(event);
            }
        }
    
        // 重置状态
        this.touchStartOnDice = false;
        this.touchStartDice = null;
        this.isDragging = false;
    }

    findClickedDiceByMesh(clickedMesh, dice) {
        // 检查当前骰子的mesh或其父级是否与点击的mesh相关
        let currentMesh = clickedMesh;
        while (currentMesh) {
            if (currentMesh === dice.mesh) {
                return true;
            }
            currentMesh = currentMesh.parent;
        }
        return false;
    }
}